import {
  assertPublicHttpsUrl,
  BlockedFetchTargetError,
} from "@inference-net/ssrf-guard";
import z from "zod";

import type { GenerationForClient } from "./generations.models";
import { GenerationForClientSchema } from "./generations.models";

// Webhook event types enum for strict typing
export enum WebhookEventType {
  Test = "webhook.test",
  GenerationCompleted = "generation.completed",
  SlowGroupCompleted = "slow-group.completed",
  AsyncEmbeddingCompleted = "async-embedding.completed",
}

// Data types
export const WebhookSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  identifier: z.string(),
  teamId: z.string(),
  url: z.string().url(),
  isDisabled: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type Webhook = z.infer<typeof WebhookSchema>;

export const WebhookTestPayloadSchema = z.object({
  event: z.literal(WebhookEventType.Test),
  timestamp: z.string(),
  webhook_id: z.string(),
  data: z.object({
    message: z.string(),
  }),
});
export type WebhookTestPayload = z.infer<typeof WebhookTestPayloadSchema>;

// Generation completion webhook payload - contains the same data as getGeneration endpoint
export const WebhookGenerationCompletionPayloadSchema = z.object({
  event: z
    .literal(WebhookEventType.GenerationCompleted)
    .or(z.literal(WebhookEventType.AsyncEmbeddingCompleted)),
  timestamp: z.string(),
  webhookId: z.string(),
  data: GenerationForClientSchema,
});
export type WebhookGenerationCompletionPayload = z.infer<
  typeof WebhookGenerationCompletionPayloadSchema
>;

/**
 * A URL a customer registers for webhook delivery: HTTPS on a public host.
 * Delivery re-checks the resolved address on every request.
 */
export const WebhookUrlSchema = z
  .string()
  .url()
  .superRefine((value, ctx) => {
    try {
      assertPublicHttpsUrl(new URL(value));
    } catch (error) {
      if (!(error instanceof BlockedFetchTargetError)) {
        throw error;
      }
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: error.message });
    }
  });

// Request/Response types
export const CreateWebhookRequest = z.object({
  name: z.string().optional(),
  url: WebhookUrlSchema,
});
export type CreateWebhookRequest = z.infer<typeof CreateWebhookRequest>;

export const CreateWebhookResponse = z.object({
  webhook: WebhookSchema,
});
export type CreateWebhookResponse = z.infer<typeof CreateWebhookResponse>;

export const ListWebhooksRequest = z.object({
  limit: z.number().optional().default(50),
  offset: z.number().optional().default(0),
});
export type ListWebhooksRequest = z.infer<typeof ListWebhooksRequest>;

export const ListWebhooksResponse = z.object({
  webhooks: z.array(WebhookSchema),
  total: z.number(),
});
export type ListWebhooksResponse = z.infer<typeof ListWebhooksResponse>;

export const TestWebhookRequest = z.object({
  webhookId: z.string(),
  eventType: z
    .nativeEnum(WebhookEventType)
    .optional()
    .default(WebhookEventType.Test),
});
export type TestWebhookRequest = z.infer<typeof TestWebhookRequest>;

export const TestWebhookResponse = z.object({
  success: z.boolean(),
  statusCode: z.number().optional(),
  error: z.string().optional(),
});
export type TestWebhookResponse = z.infer<typeof TestWebhookResponse>;

export const UpdateWebhookRequest = z.object({
  webhookId: z.string(),
  name: z.string().optional(),
  url: WebhookUrlSchema.optional(),
  isDisabled: z.boolean().optional(),
});
export type UpdateWebhookRequest = z.infer<typeof UpdateWebhookRequest>;

export const UpdateWebhookResponse = z.object({
  webhook: WebhookSchema,
});
export type UpdateWebhookResponse = z.infer<typeof UpdateWebhookResponse>;

export const DeleteWebhookRequest = z.object({
  webhookId: z.string(),
});
export type DeleteWebhookRequest = z.infer<typeof DeleteWebhookRequest>;

export const DeleteWebhookResponse = z.object({
  success: z.boolean(),
});
export type DeleteWebhookResponse = z.infer<typeof DeleteWebhookResponse>;

export const GetWebhookByIdRequest = z.object({
  webhookId: z.string(),
});
export type GetWebhookByIdRequest = z.infer<typeof GetWebhookByIdRequest>;

export const GetWebhookByIdResponse = z.object({
  webhook: WebhookSchema.nullable(),
});
export type GetWebhookByIdResponse = z.infer<typeof GetWebhookByIdResponse>;

export const GetWebhookInvocationStatsRequest = z.object({
  webhookId: z.string(),
});
export type GetWebhookInvocationStatsRequest = z.infer<
  typeof GetWebhookInvocationStatsRequest
>;

export const GetWebhookInvocationStatsResponse = z.object({
  successful: z.number(),
  failed: z.number(),
  total: z.number(),
});
export type GetWebhookInvocationStatsResponse = z.infer<
  typeof GetWebhookInvocationStatsResponse
>;

// Service request/response types that include auth
export const CreateWebhookServiceRequest = z.object({
  params: CreateWebhookRequest,
  auth: z.object({
    userId: z.string(),
    teamId: z.string(),
  }),
});
export type CreateWebhookServiceRequest = z.infer<
  typeof CreateWebhookServiceRequest
>;

export const ListWebhooksServiceRequest = z.object({
  params: ListWebhooksRequest,
  auth: z.object({
    userId: z.string(),
    teamId: z.string(),
  }),
});
export type ListWebhooksServiceRequest = z.infer<
  typeof ListWebhooksServiceRequest
>;

export const TestWebhookServiceRequest = z.object({
  params: TestWebhookRequest,
  auth: z.object({
    userId: z.string(),
    teamId: z.string(),
  }),
});
export type TestWebhookServiceRequest = z.infer<
  typeof TestWebhookServiceRequest
>;

export const UpdateWebhookServiceRequest = z.object({
  params: UpdateWebhookRequest,
  auth: z.object({
    userId: z.string(),
    teamId: z.string(),
  }),
});
export type UpdateWebhookServiceRequest = z.infer<
  typeof UpdateWebhookServiceRequest
>;

export const DeleteWebhookServiceRequest = z.object({
  params: DeleteWebhookRequest,
  auth: z.object({
    userId: z.string(),
    teamId: z.string(),
  }),
});
export type DeleteWebhookServiceRequest = z.infer<
  typeof DeleteWebhookServiceRequest
>;

export const GetWebhookByIdServiceRequest = z.object({
  params: GetWebhookByIdRequest,
  auth: z.object({
    userId: z.string(),
    teamId: z.string(),
  }),
});
export type GetWebhookByIdServiceRequest = z.infer<
  typeof GetWebhookByIdServiceRequest
>;

export const GetWebhookInvocationStatsServiceRequest = z.object({
  params: GetWebhookInvocationStatsRequest,
  auth: z.object({
    userId: z.string(),
    teamId: z.string(),
  }),
});
export type GetWebhookInvocationStatsServiceRequest = z.infer<
  typeof GetWebhookInvocationStatsServiceRequest
>;

// Service interface
export interface WebhooksServiceInterface {
  init(): Promise<void>;
  shutdown(): Promise<void>;
  createWebhook(
    request: CreateWebhookServiceRequest,
  ): Promise<CreateWebhookResponse>;
  listWebhooks(
    request: ListWebhooksServiceRequest,
  ): Promise<ListWebhooksResponse>;
  testWebhook(request: TestWebhookServiceRequest): Promise<TestWebhookResponse>;
  updateWebhook(
    request: UpdateWebhookServiceRequest,
  ): Promise<UpdateWebhookResponse>;
  deleteWebhook(
    request: DeleteWebhookServiceRequest,
  ): Promise<DeleteWebhookResponse>;
  getWebhookById(
    request: GetWebhookByIdServiceRequest,
  ): Promise<GetWebhookByIdResponse>;
  getWebhookInvocationStats(
    request: GetWebhookInvocationStatsServiceRequest,
  ): Promise<GetWebhookInvocationStatsResponse>;
}

export const WebhookSlowGroupCompletionPayloadSchema = z.object({
  event: z.literal(WebhookEventType.SlowGroupCompleted),
  timestamp: z.string(),
  groupId: z.string(),
  data: z.object({
    groupSize: z.number(),
    status: z.enum(["completed", "failed"]),
    generations: z.array(GenerationForClientSchema),
  }),
});

export type WebhookSlowGroupCompletionPayload = z.infer<
  typeof WebhookSlowGroupCompletionPayloadSchema
>;

export type WebhookCompletionPayload =
  | WebhookTestPayload
  | WebhookGenerationCompletionPayload
  | WebhookSlowGroupCompletionPayload;

/** The object a webhook event is about; it picks the id headers sent with it. */
export type WebhookSubject =
  | { kind: "generation"; generationId: string }
  | { kind: "embedding"; generationId: string }
  | { kind: "group"; groupId: string };

export const WEBHOOK_USER_AGENT = "Kuzco-Webhook/1.0";

/** Time a receiver has to answer one webhook POST, real or test. */
export const WEBHOOK_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Headers sent with every webhook POST, by real deliveries and by the
 * dashboard test alike. Embedding events carry both id headers: the docs name
 * `X-Inference-Embedding-ID`, and earlier deliveries sent only
 * `X-Inference-Generation-ID`.
 */
export function buildWebhookRequestHeaders({
  eventType,
  webhookIdentifier,
  subject,
  attempt,
}: {
  eventType: WebhookEventType;
  webhookIdentifier: string;
  subject: WebhookSubject | null;
  attempt: number;
}): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "User-Agent": WEBHOOK_USER_AGENT,
    "X-Inference-Event": eventType,
    "X-Inference-Webhook-ID": webhookIdentifier,
    "X-Inference-Delivery-Attempt": String(attempt),
  };
  switch (subject?.kind) {
    case "generation":
      headers["X-Inference-Generation-ID"] = subject.generationId;
      break;
    case "embedding":
      headers["X-Inference-Generation-ID"] = subject.generationId;
      headers["X-Inference-Embedding-ID"] = subject.generationId;
      break;
    case "group":
      headers["X-Inference-Group-ID"] = subject.groupId;
      break;
    case undefined:
      break;
  }
  return headers;
}

const TEST_MODEL = "test-model";
const TEST_EMBEDDING_MODEL = "test-embedding-model";
const TEST_EMBEDDING_DIMENSIONS = 768;

/**
 * Sample payload for the dashboard "Test" button. Ids carry a `test-` prefix
 * so receivers can tell a test from a real event.
 */
export function buildWebhookTestPayload({
  eventType,
  webhookIdentifier,
  now,
}: {
  eventType: WebhookEventType;
  webhookIdentifier: string;
  now: Date;
}): { payload: WebhookCompletionPayload; subject: WebhookSubject | null } {
  const timestamp = now.toISOString();
  const createdAt = new Date(now.getTime() - 15_000).toISOString();
  const dispatchedAt = new Date(now.getTime() - 10_000).toISOString();
  const firstChunkAt = new Date(now.getTime() - 9_000).toISOString();
  const createdSeconds = Math.floor(now.getTime() / 1000);
  const chatUsage = {
    prompt_tokens: 15,
    completion_tokens: 20,
    total_tokens: 35,
  };

  const testGeneration = (id: string): GenerationForClient => ({
    id,
    state: "Success",
    stateMessage: "Test generation completed successfully",
    model: TEST_MODEL,
    createdAt,
    dispatchedAt,
    firstChunkAt,
    finishedAt: timestamp,
    usage: chatUsage,
    request: {
      model: TEST_MODEL,
      messages: [{ role: "user", content: "This is a test request" }],
      n: 1,
      temperature: 1,
      top_p: 1,
    },
    response: {
      id,
      object: "chat.completion",
      created: createdSeconds,
      model: TEST_MODEL,
      choices: [
        {
          index: 0,
          message: { role: "assistant", content: "This is a test response" },
          finish_reason: "stop",
          logprobs: null,
        },
      ],
      usage: chatUsage,
    },
  });

  switch (eventType) {
    case WebhookEventType.Test:
      return {
        payload: {
          event: WebhookEventType.Test,
          timestamp,
          webhook_id: webhookIdentifier,
          data: { message: "This is a test event from Inference.net" },
        },
        subject: null,
      };
    case WebhookEventType.GenerationCompleted: {
      const generationId = `test-generation-${now.getTime()}`;
      return {
        payload: {
          event: WebhookEventType.GenerationCompleted,
          timestamp,
          webhookId: webhookIdentifier,
          data: testGeneration(generationId),
        },
        subject: { kind: "generation", generationId },
      };
    }
    case WebhookEventType.SlowGroupCompleted: {
      const groupId = `test-group-${now.getTime()}`;
      return {
        payload: {
          event: WebhookEventType.SlowGroupCompleted,
          timestamp,
          groupId,
          data: {
            groupSize: 2,
            status: "completed",
            generations: [
              testGeneration(`test-generation-${now.getTime()}-1`),
              testGeneration(`test-generation-${now.getTime()}-2`),
            ],
          },
        },
        subject: { kind: "group", groupId },
      };
    }
    case WebhookEventType.AsyncEmbeddingCompleted: {
      const generationId = `test-embedding-${now.getTime()}`;
      const embeddingUsage = { prompt_tokens: 8, total_tokens: 8 };
      return {
        payload: {
          event: WebhookEventType.AsyncEmbeddingCompleted,
          timestamp,
          webhookId: webhookIdentifier,
          data: {
            id: generationId,
            state: "Success",
            stateMessage: "Test embedding completed successfully",
            model: TEST_EMBEDDING_MODEL,
            createdAt,
            dispatchedAt,
            firstChunkAt,
            finishedAt: timestamp,
            usage: embeddingUsage,
            request: null,
            response: {
              id: generationId,
              object: "list",
              model: TEST_EMBEDDING_MODEL,
              data: [
                {
                  object: "embedding",
                  index: 0,
                  embedding: Array.from(
                    { length: TEST_EMBEDDING_DIMENSIONS },
                    (_, index) => Math.sin(index),
                  ),
                },
              ],
              usage: embeddingUsage,
            },
          },
        },
        subject: { kind: "embedding", generationId },
      };
    }
  }
}
